import SalesLead, {
  SALES_LEAD_SYSTEM_FIELDS,
} from "../../../classes/SalesLead";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { call } from "../../../util/misc";

export const GET_SALES_LEAD = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: SalesLead,
    path: SalesLead.getPath(id),
    stateKey: "salesLeads",
    id,
  });
};

export const GET_SALES_LEADS = (
  { dispatch },
  { conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: SalesLead,
    stateKey: "salesLeads",
    path: SalesLead.getPath(),
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_SALES_LEAD = async (
  { dispatch },
  { id, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: SalesLead,
    path: SalesLead.getPath(id),
    stateKey: "salesLeads",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SALES_LEADS = (
  { dispatch },
  { listenerPath, filter, where, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: SalesLead,
    path: SalesLead.getPath(),
    stateKey: "salesLeads",
    conditions: where || ["archived", "==", filter === "archive"],
    listenerPath:
      listenerPath ||
      (filter === "archive" ? "archived-sales-leads" : "recent-sales-leads"),
    next,
    error,
    unlisten,
  });
};

export const LISTEN_PROJECT_SALES_LEADS = (
  { dispatch },
  { projectID, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: SalesLead,
    path: SalesLead.getPath(),
    listenerPath: projectID + SalesLead.getPath(),
    stateKey: "salesLeads",
    conditions: [
      ["projectID", "==", projectID],
      ["isSold", "==", false],
    ],
    next,
    error,
    unlisten,
  });
};

export const SAVE_SALES_LEAD = async ({ dispatch, state }, lead) => {
  let path;
  const saveDocuments = (existingData) => {
    return Promise.all([
      dispatch("SAVE_DOCUMENTS", {
        parentData: lead.data,
        fieldPath: "plans",
        folderPath: `${path}/plans`,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: lead.data,
        fieldPath: "status.outcome.notaxCert",
        folderPath: `${path}/tax-exempt-cert`,
        isSingle: true,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: lead.data,
        fieldPath: "status.drawings",
        folderPath: `${path}/drawings`,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: lead.data,
        fieldPath: "status.outcome.intentLetter",
        folderPath: `${path}/intent-letter`,
        isSingle: true,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: lead.data,
        fieldPath: "status.outcome.contracts",
        folderPath: `${path}/contracts`,
        existingData,
      }),
    ]);
  };

  if (!lead.id) {
    // New Lead
    lead.data.created = new Date();
    lead.data.createdBy = state.user.id;
    const ref = doc(collection(db, SalesLead.getPath()));
    lead.id = ref.id;
    path = lead.path;

    // Upload new files
    await saveDocuments();

    await setDoc(doc(db, path).withConverter(SalesLead), lead);

    // If this lead is linked to a service issue, update the issue's salesLeadID
    if (lead.data.serviceIssueID) {
      await dispatch("UPDATE_SERVICE_ISSUE", {
        issueID: lead.data.serviceIssueID,
        changes: { salesLeadID: lead.id },
      });
    }

    return lead;
  } else {
    // Existing Lead
    lead.data.modified = new Date();
    lead.data.modifiedBy = state.user.id;
    const existingData = await dispatch("GET_SALES_LEAD", lead.id);
    path = lead.path;

    // Preserve system-managed fields owned by the cloud functions
    // (_createSale / _createProject / _changeSale). These must never be
    // written from the editable buffer: a stale draft once overwrote isSold
    // and saleID with their defaults, severing a sold lead's link to its sale.
    // Exception: projectID is also set client-side when the user confirms a
    // project match in SalesLeadEdit (matchProject). If the server doc has no
    // projectID yet there is no authoritative value to preserve, so keep the
    // buffer's — otherwise the confirmed association is silently discarded
    // and _createSale orphans the sale from the matched project.
    SALES_LEAD_SYSTEM_FIELDS.forEach((field) => {
      if (field === "projectID" && !existingData.data[field]) {
        return;
      }
      lead.data[field] = existingData.data[field];
    });

    // Upload new files and delete removed files
    await saveDocuments(existingData.data);

    // Update
    await setDoc(doc(db, path).withConverter(SalesLead), lead);
    return lead;
  }
};

export const UPDATE_SALES_LEAD = ({ state }, { id, changes }) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  return updateDoc(doc(db, SalesLead.getPath(id)), changes);
};

export const DELETE_SALES_LEAD = async ({ commit }, id) => {
  await deleteDoc(doc(db, SalesLead.getPath(id)));
  return commit("REMOVE", { stateKey: "salesLeads", id });
};

export const PREPARE_FOR_CHANGE_ORDER = async (context, salesLeadID) => {
  await call("prepareForChangeOrder", salesLeadID);
};
